import { Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { InformationRow, StatusBadge, designTokens } from '../../../design-system';
import { candidateParts, mappingCandidates, previewValue } from '../model/mappings';
import type { ProfileMappingRule, SourcePropertyDefinition, TargetFieldDefinition } from '../model/types';

export function MappingRuleSummary({ target, rule, sources, action }: {
  target: TargetFieldDefinition;
  rule: ProfileMappingRule;
  sources: SourcePropertyDefinition[];
  action?: ReactNode;
}) {
  const candidates = mappingCandidates(rule);
  return <Box data-testid={`mapping-row-${target.key}`} sx={{
    display: 'grid', minWidth: 0, alignItems: 'start',
    gridTemplateColumns: { xs: action ? 'minmax(0, 1fr) auto' : 'minmax(0, 1fr)', md: action ? 'minmax(0, 0.7fr) minmax(0, 2fr) auto' : 'minmax(0, 0.7fr) minmax(0, 2fr)' },
    gap: `${designTokens.space.md}px`,
  }}>
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.75} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography component="h3" sx={{ ...designTokens.typography.ui, fontWeight: 600, overflowWrap: 'anywhere' }}>{target.label}</Typography>
        {target.required && <StatusBadge label="필수" tone="neutral" />}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>{target.description}</Typography>
    </Box>
    <Box component="dl" sx={{
      m: 0, minWidth: 0, display: 'grid',
      gridColumn: { xs: '1 / -1', md: 2 }, gridRow: { xs: 2, md: 1 },
      gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, 1fr)' },
      gap: `${designTokens.space.md}px`,
    }}>
      <InformationRow
        label="연결 속성"
        layout="stacked"
        value={candidates.length ? <Box component="span">
          {candidates.map((candidate, index) => <Box component="span" key={index} sx={{ display: 'block', mt: index > 0 ? 0.75 : 0 }}>
            {index > 0 && <Box component="span" sx={{ color: 'text.secondary' }}>없으면 </Box>}
            <Box component="span" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {candidateParts(candidate).map((part, partIndex) => part.sourceProperty
                ? <Box key={partIndex} component="span" sx={{ color: designTokens.color.actionDark }}>[{sources.find((source) => source.key === part.sourceProperty)?.label ?? part.sourceProperty}]</Box>
                : <Box key={partIndex} component="span">{part.text}</Box>)}
            </Box>
            {candidateParts(candidate).filter((part) => part.sourceProperty).length > 1 && <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {candidate.requireAll ? '모든 속성에 값이 있어야 사용' : '빈 속성은 생략'}
            </Typography>}
          </Box>)}
        </Box> : '연결된 직원 속성 없음'}
        description={candidates.length > 1 ? '값이 없으면 다음 값 사용' : candidates[0] && candidateParts(candidates[0]).length > 1 ? '표시 형식 적용' : '속성 사용'}
      />
      <InformationRow label="예시 결과" layout="stacked" value={previewValue(rule, sources) || '값 없음'} emphasizeValue />
    </Box>
    {action && <Box sx={{ gridColumn: { xs: 2, md: 3 }, gridRow: 1, justifySelf: 'end' }}>{action}</Box>}
  </Box>;
}
